import { Editor, TLParentId, TLShapeId } from "@tldraw/tldraw";
import { capitalize } from "lodash";
import CONFIG from "config/config";
import { authorization } from "module/auth";

import { SECTIONS } from "./constants";
import { getOrCreateAnonymousId } from "./utils/liveblocksAuth";

export function isKeyLetterOrNumber(event: KeyboardEvent): boolean {
  return /[a-zA-Z0-9-_ ]/.test(event.key);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export const convertToInch = (num: number) => Math.round((num / 25.4) * 100) / 100;

export const getFlyerSizeDisplay = (width: number, height: number, isInInch: boolean) => {
  if (isInInch) {
    return `${convertToInch(width)}” x ${convertToInch(height)}”`;
  } else {
    return `${width} x ${height} mm`;
  }
};

export const getFlyerWidthDisplay = (width: number, isInInch: boolean) => {
  if (isInInch) {
    return `${convertToInch(width)}”`;
  } else {
    return `${width} mm`;
  }
};

export const getFlyerHeightDisplay = (height: number, isInInch: boolean) => {
  if (isInInch) {
    return `${convertToInch(height)}”`;
  } else {
    return `${height} mm`;
  }
};

// Helper function to detect measurement system from browser locale
export const detectMeasurementSystemFromLocale = (): "inches" | "mm" => {
  try {
    // Try to get the most accurate locale information
    const userLocale = navigator.language || navigator.languages?.[0] || "en-US";

    // Use Intl.Locale to get more detailed locale information
    const locale = new Intl.Locale(userLocale);
    const region = locale.region?.toUpperCase();

    // Countries that primarily use imperial/inches:
    // US, Liberia, Myanmar (Burma) - but we'll focus on US and some US territories
    // Canada uses a mix but officially metric, however many users prefer inches for some measurements
    const imperialRegions = ["US", "LR", "MM"];
    const mixedRegions = ["CA"]; // Canada uses both, but many prefer inches for design/print

    // For design/printing context, include Canada as inches preference
    if (region && (imperialRegions.includes(region) || mixedRegions.includes(region))) {
      return "inches";
    }

    // Alternative method: try to detect through number formatting preferences
    // Some browsers expose measurement preferences through Intl APIs
    if (typeof Intl !== "undefined" && Intl.Locale) {
      try {
        // Try to detect if the user's system uses imperial measurements
        // by checking common measurement-related locale properties
        const testLocale = new Intl.Locale(userLocale);
        if (testLocale.region === "US" || userLocale.includes("en-US")) {
          return "inches";
        }
      } catch (e) {
        // Fallback if Intl.Locale fails
      }
    }

    return "mm"; // Default to metric for most of the world
  } catch (error) {
    // Fallback if any of the above fails
    console.warn("Could not detect measurement system from locale:", error);
    return navigator.language?.includes("en-US") ? "inches" : "mm";
  }
};

export const displayInInch = (countryCode: string, userPreference?: string) => {
  // If user has a preference and it's valid, use it
  if (userPreference && (userPreference === "inches" || userPreference === "mm")) {
    return userPreference === "inches";
  }

  // If no user preference, detect from browser locale
  if (!userPreference) {
    const detectedUnit = detectMeasurementSystemFromLocale();
    return detectedUnit === "inches";
  }

  // Fallback to country code parameter for backward compatibility
  return countryCode === "US" || countryCode === "CA";
};

export function getFontWeight(value: string): number {
  switch (value) {
    case "Bold":
      return 700;
    case "Semi-Bold":
      return 600;
    case "Normal":
      return 400;
    case "Light":
      return 300;
    default:
      throw new Error("Invalid font weight value");
  }
}
export function getFontWeightName(value: number): string {
  // Normalize font weight to valid values
  const normalizedWeight = normalizeFontWeight(value);

  switch (normalizedWeight) {
    case 700:
      return "Bold";
    case 600:
      return "Semi-Bold";
    case 400:
      return "Normal";
    case 300:
      return "Light";
    default:
      return "Normal"; // Fallback to Normal instead of throwing error
  }
}

/**
 * Normalize font weight to valid CSS values
 * @param weight The font weight to normalize
 * @returns A valid font weight (300, 400, 600, or 700)
 */
export function normalizeFontWeight(weight: number): number {
  if (typeof weight !== "number" || isNaN(weight)) {
    return 400; // Default to Normal
  }

  // Map common font weights to valid values
  if (weight <= 350) return 300; // Light
  if (weight <= 450) return 400; // Normal
  if (weight <= 650) return 600; // Semi-Bold
  return 700; // Bold
}

export const getClosestContainer = (
  shapeFrontId: TLParentId,
  shapeBackId: TLParentId,
  editor: Editor,
  selectedShape: any
) => {
  const shapeFront = editor.getShape(shapeFrontId);
  const shapeBack = editor.getShape(shapeBackId);

  if (!shapeFront || !shapeBack || !selectedShape) return null;

  // Use getShapePageBounds for all shapes — works for groups, text, images, etc.
  // Groups don't have props.w/h, so the old approach (selectedShape.props.w) returned NaN for groups.
  const selectedBounds = editor.getShapePageBounds(selectedShape.id);
  const frontBounds = editor.getShapePageBounds(shapeFrontId as unknown as TLShapeId);
  const backBounds = editor.getShapePageBounds(shapeBackId as unknown as TLShapeId);

  if (!selectedBounds || !frontBounds || !backBounds) return null;

  const selectedCenterX = selectedBounds.x + selectedBounds.w / 2;
  const selectedCenterY = selectedBounds.y + selectedBounds.h / 2;

  const frontCenterX = frontBounds.x + frontBounds.w / 2;
  const frontCenterY = frontBounds.y + frontBounds.h / 2;

  const backCenterX = backBounds.x + backBounds.w / 2;
  const backCenterY = backBounds.y + backBounds.h / 2;

  const distanceToFront = Math.sqrt(
    Math.pow(selectedCenterX - frontCenterX, 2) + Math.pow(selectedCenterY - frontCenterY, 2)
  );

  const distanceToBack = Math.sqrt(
    Math.pow(selectedCenterX - backCenterX, 2) + Math.pow(selectedCenterY - backCenterY, 2)
  );

  return distanceToFront < distanceToBack ? shapeFront : shapeBack;
};

export function getOpacityPercentage(color: string) {
  if (!color) {
    return 100;
  }

  if (color.startsWith("#") && color.length === 9) {
    const alphaHex = color.slice(7, 9);
    const alphaDecimal = parseInt(alphaHex, 16);
    return Math.round((alphaDecimal / 255) * 100);
  }

  if (color.startsWith("rgba")) {
    const alpha = parseFloat(color.split(",")[3].replace(")", "").trim());
    return Math.round(alpha * 100);
  }

  return 100;
}

export function colorToHex(color: string): string {
  const ctx = document.createElement("canvas").getContext("2d");
  if (ctx) {
    ctx.fillStyle = color;
    const rgbaColor = ctx.fillStyle;
    if (rgbaColor.startsWith("rgb")) {
      const parts = rgbaColor.match(/\d+/g);
      if (parts) {
        const r = parseInt(parts[0]).toString(16).padStart(2, "0");
        const g = parseInt(parts[1]).toString(16).padStart(2, "0");
        const b = parseInt(parts[2]).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`;
      }
    }
  }
  //@ts-ignore
  return ctx.fillStyle;
}

export function setOpacityPercentage(color: string, opacity: number): string {
  color = colorToHex(color);
  if (color.startsWith("#") && (color.length === 7 || color.length === 9)) {
    const alphaHex = Math.round((opacity / 100) * 255)
      .toString(16)
      .padStart(2, "0");
    return color.length === 7 ? `${color}${alphaHex}` : `${color.slice(0, 7)}${alphaHex}`;
  }

  return color;
}

export const getBorderLabel = (props: any, fullWord = false) => {
  const sides: string[] = [];
  if (props.isShowBorderTop) sides.push(fullWord ? "Top" : "T");
  if (props.isShowBorderBottom) sides.push(fullWord ? "Bottom" : "B");
  if (props.isShowBorderLeft) sides.push(fullWord ? "Left" : "L");
  if (props.isShowBorderRight) sides.push(fullWord ? "Right" : "R");

  if (sides.length === 4) {
    return fullWord ? "All" : <p className="text-black text[16px]">All</p>;
  }

  if (sides.length === 1) {
    return sides[0];
  }
  // if need to show all sides
  // return sides.length > 0 ? sides.join(", ") : null;

  return null;
};

export function getShapeName(editor: Editor, shapeId: TLShapeId, index = 0) {
  const shape = editor.getShape(shapeId);

  if (!shape) return "Unknown shape";

  // Use meta safely - tldraw shape objects are frozen/immutable
  const meta: any = shape.meta || {};

  // Helper function to get the next sequential number for a shape type
  const getNextShapeNumber = (shapeType: string, excludeShapeId?: string): number => {
    const snapshot = editor.getSnapshot();
    const allShapes = Object.values(snapshot.document.store).filter((s: any) => {
      // Exclude the current shape from counting
      if (excludeShapeId && s.id === excludeShapeId) {
        return false;
      }
      // Normalize shape types for counting
      const normalizedType = normalizeShapeType(s.type);
      const normalizedCurrentType = normalizeShapeType(shapeType);
      return normalizedType === normalizedCurrentType;
    });

    // Extract numbers from existing shape names
    const existingNumbers: number[] = [];
    allShapes.forEach((s: any) => {
      const shapeName = getShapeDisplayName(s.type);
      // Try to extract number from meta.name if it matches the pattern
      if (s.meta?.name) {
        const nameMatch = s.meta.name.match(new RegExp(`^${shapeName}\\s*(\\d+)$`, "i"));
        if (nameMatch) {
          existingNumbers.push(parseInt(nameMatch[1], 10));
        }
      }
    });

    // Find the next available number starting from 1
    if (existingNumbers.length === 0) {
      return 1;
    }

    const sortedNumbers = [...existingNumbers].sort((a, b) => a - b);
    // Find the first gap, or use max + 1
    for (let i = 0; i < sortedNumbers.length; i++) {
      if (sortedNumbers[i] !== i + 1) {
        return i + 1;
      }
    }
    return sortedNumbers[sortedNumbers.length - 1] + 1;
  };

  // Helper function to normalize shape types (customRect and rectangle are the same)
  const normalizeShapeType = (type: string): string => {
    if (type === "customRect" || type === "rectangle") {
      return "rectangle";
    }
    return type;
  };

  // Helper function to get display name for shape type
  const getShapeDisplayName = (type: string): string => {
    const normalizedType = normalizeShapeType(type);

    // Map shape types to display names
    const typeMap: Record<string, string> = {
      rectangle: "Rectangle",
      customRect: "Rectangle",
      customEllipse: "Ellipse",
      ellipse: "Ellipse",
      customTriangle: "Polygon",
      triangle: "Polygon",
      customStar: "Star",
      star: "Star",
      customHeart: "Heart",
      heart: "Heart",
      customDiamond: "Diamond",
      diamond: "Diamond",
      customHexagon: "Hexagon",
      hexagon: "Hexagon",
      customPentagon: "Pentagon",
      pentagon: "Pentagon",
      customOctagon: "Octagon",
      octagon: "Octagon",
      customCloud: "Cloud",
      cloud: "Cloud",
      customRhombus: "Rhombus",
      rhombus: "Rhombus",
      customTrapezoid: "Trapezoid",
      trapezoid: "Trapezoid",
      customXBox: "XBox",
      xBox: "XBox",
      customArrowUp: "Arrow",
      customArrowDown: "Arrow",
      customArrowLeft: "Arrow",
      customArrowRight: "Arrow",
      arrowUp: "Arrow",
      arrowDown: "Arrow",
      arrowLeft: "Arrow",
      arrowRight: "Arrow",
    };

    return typeMap[normalizedType] || capitalize(normalizedType);
  };

  // Check for specific shape ids and set name
  if (shapeId === "shape:frontBorderDashLine" || shapeId === "shape:backBorderDashLine") {
    return "Safety line";
  }
  if (shapeId === "shape:frontRedLine" || shapeId === "shape:backRedLine") {
    return "Cut line";
  }
  if (shapeId === "shape:frontBlackLine" || shapeId === "shape:backBlackLine") {
    return "Bleeds line";
  }
  if (shapeId === ("shape:addressBox" as TLShapeId)) {
    return "Address Box";
  }

  // Check for QR code shapes BEFORE checking meta.name to ensure QR codes always show correct name
  const shapeProps = shape.props as any;
  const assetIdStr = shapeProps?.assetId ? String(shapeProps.assetId) : "";

  // Explicitly exclude rectangles from QR Code detection
  const isRectShape = shape.type === "customRect" || shape.type === "rectangle";

  const isQRCodeShape =
    !isRectShape &&
    (shapeId === "shape:qrCode" ||
      String(shapeId).includes("qrCode") ||
      (shape.type === "customImage" && shapeProps?.qrCodeLink) ||
      (shape.type === "customImage" &&
        assetIdStr.toLowerCase().includes("qr") &&
        !assetIdStr.toLowerCase().includes("rect")));

  if (isQRCodeShape) {
    return "QR Code";
  }

  // Check for text shapes - don't add ID number
  const isTextShape = shape.type === "text" || shape.type === "customText";

  if (shape.type === "group") {
    // If the group already has a name, return it (don't override manual names)
    if (meta.name && typeof meta.name === "string" && meta.name.trim() !== "") {
      return meta.name;
    }

    const snapshot = editor.getSnapshot();
    const allShapes = Object.values(snapshot.document.store).filter((s: any) => s.type === "group");

    // Get all existing group names that start with "Group"
    const existingGroupNames = allShapes
      .map((s: any) => s.meta?.name || "")
      .filter((name: string) => name.startsWith("Group"))
      .map((name: string) => {
        const match = name.match(/^Group\s*(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((num): num is number => num !== null);

    // Find the next available number
    let nextGroupNumber = 1;
    if (existingGroupNames.length > 0) {
      // Sort existing numbers and find the first gap, or use max + 1
      const sortedNumbers = [...existingGroupNames].sort((a: number, b: number) => a - b);
      for (let i = 0; i < sortedNumbers.length; i++) {
        if (sortedNumbers[i] !== i + 1) {
          nextGroupNumber = i + 1;
          break;
        }
        nextGroupNumber = sortedNumbers[i] + 1;
      }
    }

    // Assign a unique name to the group shape's meta
    const newName = `Group ${nextGroupNumber}`;
    editor.updateShape({ ...shape, meta: { ...meta, name: newName } });

    return newName;
  }

  // Check for QR code again here in case meta.name was set but we still want to override it
  const shapePropsForName = shape.props as any;
  const assetIdStrForName = shapePropsForName?.assetId ? String(shapePropsForName.assetId) : "";

  // Explicitly exclude rectangles from QR Code detection
  const isRectShapeForName = shape.type === "customRect" || shape.type === "rectangle";

  const isQRCodeForName =
    !isRectShapeForName &&
    (shapeId === "shape:qrCode" ||
      String(shapeId).includes("qrCode") ||
      (shape.type === "customImage" && shapePropsForName?.qrCodeLink) ||
      (shape.type === "customImage" &&
        assetIdStrForName.toLowerCase().includes("qr") &&
        !assetIdStrForName.toLowerCase().includes("rect")));

  if (isQRCodeForName) {
    return "QR Code";
  }

  // If it's a mask result shape with an explicit name, keep it
  if (meta.isMaskShape && typeof meta.name === "string" && meta.name.trim() !== "") {
    return meta.name;
  }

  // For text shapes, return without ID number
  if (isTextShape) {
    let shapeName =
      (meta.name as string) ||
      // @ts-ignore
      (shape.props.geo as string) ||
      editor.getShapeUtil(shape).getText(shape) ||
      capitalize(shape.type + " shape");

    // Remove "Custom" prefix if present
    if (shapeName.startsWith("Custom")) {
      shapeName = shapeName.replace("Custom", "");
    }
    // Remove "shape" suffix if present
    shapeName = shapeName.replace(/\s*shape\s*$/i, "");
    return shapeName;
  }

  // Get the display name for this shape type
  const displayName = getShapeDisplayName(shape.type);

  // Check if shape already has a name assigned
  if (meta.name && typeof meta.name === "string") {
    // If the user manually renamed this shape, always respect their custom name
    if (meta.isCustomName) {
      return meta.name;
    }

    const nameMatch = meta.name.match(new RegExp(`^${displayName}\\s*(\\d+)$`, "i"));
    if (nameMatch) {
      // Check if another shape (not this one) already has this exact name
      const snapshot = editor.getSnapshot();
      const allShapes = Object.values(snapshot.document.store);
      const shapesWithSameName = allShapes.filter((s: any) => {
        return s.id !== shapeId && s.meta?.name === meta.name;
      });

      // If the name is not used by another shape, keep it
      if (shapesWithSameName.length === 0) {
        return meta.name;
      }

      // If there are other shapes with the same name, resolve the conflict
      // The shape with the lowest ID (created first) keeps the name
      // The shape with a higher ID (pasted later) gets a new name
      const shouldKeepName = shapesWithSameName.every((s: any) => {
        // Compare IDs - if this shape's ID is "less than" the other shape's ID, it was created first
        // This ensures the original shape keeps its name and the pasted shape gets a new one
        return shapeId < s.id;
      });

      if (shouldKeepName) {
        return meta.name;
      }
      // Otherwise, this shape (the pasted one) needs a new name
    }
  }

  // Get the next sequential number for this shape type (excluding current shape)
  const shapeNumber = getNextShapeNumber(shape.type, shapeId);

  // Create the new name
  const newName = `${displayName} ${shapeNumber}`;

  // Save the name to the shape's meta
  if (!meta.name || meta.name !== newName) {
    editor.updateShape({ ...shape, meta: { ...meta, name: newName } });
  }

  // Return the formatted name with number
  return newName;
}

export const pairArtWorkLines = [
  { front: "shape:frontBorderDashLine", back: "shape:backBorderDashLine", name: "Safety lines" },
  { front: "shape:frontRedLine", back: "shape:backRedLine", name: "Cut lines" },
  { front: "shape:frontBlackLine", back: "shape:backBlackLine", name: "Bleeds lines" },
];

/** Hide selection outline for 0.5s then show again (isShowIndicator false = hide, true = show). */
/** Only runs for shapes that support isShowIndicator (custom shapes); arrow/line do not. */
export const hideSelectionTemporarily = (shape: any, editor: Editor) => {
  if (!shape || !shape.props) return;
  if (!("isShowIndicator" in shape.props)) return;

  // Read current shape from store to avoid overwriting recent prop changes (e.g. color updates)
  const current = editor.getShape(shape.id);
  if (!current || !("isShowIndicator" in current.props)) return;

  editor.updateShape({
    id: current.id,
    type: current.type,
    props: { ...current.props, isShowIndicator: false },
  });
  setTimeout(() => {
    const latest = editor.getShape(shape.id);
    if (latest && "isShowIndicator" in latest.props)
      editor.updateShape({
        id: latest.id,
        type: latest.type,
        props: { ...latest.props, isShowIndicator: true },
      });
  }, 1000);
};

export const updateShapeMouseDown = (shape: any, editor: Editor) => {
  if (!shape || !shape.props) return;
  if (!("isShowIndicator" in shape.props)) return;

  editor.updateShape({
    ...shape,
    props: {
      ...shape.props,
      isShowIndicator: true,
    },
  });
};

export const updateShapeMouseUp = (shape: any, editor: Editor) => {
  if (!shape || !shape.props) return;
  if (!("isShowIndicator" in shape.props)) return;

  setTimeout(() => {
    editor.updateShape({
      ...shape,
      props: {
        ...shape.props,
        isShowIndicator: false,
      },
    });
  }, 2000);
};

export function mmToPx(mm: number): number {
  return mm * 3.7795;
}

// to show images component in sidebar
export function getDisplayStyle(selectedSection: string): "block" | "none" {
  return selectedSection === SECTIONS.IMAGES ? "block" : "none";
}

// to show icons component in sidebar
export function getDisplayStyleIcons(selectedSection: string): "block" | "none" {
  return selectedSection === SECTIONS.ICONS ? "block" : "none";
}

export const getRoomId = (designId: string) => {
  // Guests need their roomId namespaced under their anonId so the Liveblocks
  // auth endpoint can grant them access to their own draft only. Authed
  // users keep the original format unchanged.
  const isGuest = !authorization.getUser?.()?.id;
  const guestSegment = isGuest ? `guest_${getOrCreateAnonymousId().replace(/^anonymous-/, "")}_` : "";
  if (CONFIG.ENV === "production") {
    return `029_${CONFIG.ENV}_${guestSegment}${designId}`;
  }
  return `${CONFIG.ENV}_${guestSegment}${designId}`;
};

/**
 * Authed-form roomId regardless of the current auth state. Used during the
 * post-signup claim flow: we need the *future* (authed) roomId of the
 * design so we can copy the guest's FlyerBuilderState into it before the
 * SignupModal hands control back to oppizi-platform.
 */
export const getAuthedRoomId = (designId: string | number) => {
  if (CONFIG.ENV === "production") {
    return `029_${CONFIG.ENV}_${designId}`;
  }
  return `${CONFIG.ENV}_${designId}`;
};

/**
 * Symmetric helper for the guest case — same anonId-prefixed format as
 * `getRoomId` but without depending on the (about-to-flip) auth state.
 * Useful when we need to compute the *previous* (guest) roomId after the
 * user has just been authenticated.
 */
export const getGuestRoomId = (designId: string) => {
  const guestSegment = `guest_${getOrCreateAnonymousId().replace(/^anonymous-/, "")}_`;
  if (CONFIG.ENV === "production") {
    return `029_${CONFIG.ENV}_${guestSegment}${designId}`;
  }
  return `${CONFIG.ENV}_${guestSegment}${designId}`;
};
